import { css } from '@emotion/core';
import { useMediaQuery } from 'react-responsive';
import { Button } from 'react-bootstrap';
import React, { useState, useEffect, useImperativeHandle, forwardRef, MutableRefObject, useRef, useCallback } from 'react';
import t from 'react-translate';
import useTimer from 'recording/hooks/use-timer';
import useCountdownBeep from 'recording/hooks/use-countdown-beep';
import useMountEffect from 'shared/hooks/use-mount-effect';
import useMediaRecorder from 'recording/hooks/use-media-recorder';
import { MediaSourceType, requestMediaStream, stopMediaStreams } from 'recording/services/media-stream';
import VideoStreamPreview from 'recording/components/video-stream-preview';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import {
  VideoPracticeScenario,
  VideoPracticeActivity,
  VideoPracticeOption,
  VideoPracticeType,
  PracticeAgain,
} from 'redux/schemas/models/video-practice';
import { black, danger, hexToRgbaString, shade } from 'styles/global_defaults/colors';
import {
  quarterSpacing, halfSpacing, standardSpacing, largeSpacing,
  doubleSpacing, tripleSpacing, extraLargeSpacing,
} from 'styles/global_defaults/scaffolding';
import { handheld, largeDesktop, screenXsMax } from 'styles/global_defaults/media-queries';
import NvPopover from 'shared/components/nv-popover';
import ClickableContainer from 'components/clickable-container';
import { PercentageIndicator, RecordingIndicator } from 'recording/components/recording-indicator';
import { getTimeLimits, roundToMinutes } from './video-practice-utils';
import PracticeActivityProgress from './practice-activity-progress';
import VideoPracticeTitle from './video-practice-title';
import { RecordMode } from './practice-activity-modal';
import CountdownTimer from './countdown-timer';
import { config } from '../../../../config/pendo.config.json';

export type OnStopRecordingParams = {
  blob: Blob;
  timeElapsed: number;
};

type RecordPracticeActivityProps = {
  practiceType: VideoPracticeType;
  modalStepIndex: number;
  onStopRecording: (params: OnStopRecordingParams) => void;
  onRetry: () => void;
  scenario: VideoPracticeScenario;
  activity?: VideoPracticeActivity;
  take: number;
  mode?: RecordMode;
  selectRecordingMode: () => void;
};

enum RecordState {
  NOT_STARTED = 'notStarted',
  RUNNING = 'running',
  PAUSED = 'paused',
  COUNTING_DOWN = 'countingDown',
  DONE = 'done',
}

const RecordPracticeActivity = forwardRef<any, RecordPracticeActivityProps>(({
  practiceType,
  modalStepIndex,
  onRetry,
  onStopRecording,
  scenario,
  activity,
  take,
  mode,
  selectRecordingMode,
}, ref: MutableRefObject<any>) => {
  const isHandheld = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const [recordState, setRecordState] = useState<RecordState>(RecordState.NOT_STARTED);

  const styles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${hexToRgbaString(black, 0.95)};
    flex: 1;
    ${largeDesktop(css`
      overflow: hidden;
    `)}

    .practice-activity-progress {
      margin-top: 60px;
      margin-bottom: 50px;
    }

    .course-title-small {
      margin-bottom: ${largeSpacing}px;
    }

    .details {
      display: flex;
      justify-content: space-between;
      width: 720px;
      margin-bottom: ${quarterSpacing}px;

      .video-practice-title {
        margin-right: 100px;
      }
    }

    .record-activity {
    ${largeDesktop(css`
      min-height: 0;
    `)}
      max-height: 405px;
      position: relative;
      ${mode === RecordMode.SCREEN && `
        max-width: 720px;
        width: 100%;
        height: 480px;
        background: ${recordState === RecordState.RUNNING ? '#597393' : ''};
      `}
      .video-stream-preview {
        width: 100%;
        ${mode === RecordMode.SCREEN && `
        .video-wrapper {
          position: initial;
          video {
            position: absolute;
            bottom: 0;
            right: 0;
          }
        }`}
      }
      .screen-recording-desc {
        padding: ${tripleSpacing}px ${extraLargeSpacing * 2}px;
        z-index: 1;
      }

      .when-ready-desc {
        z-index: 1;
      }

      .video-stream-mask {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${hexToRgbaString(shade, 0.7)};
      }

      .record-countdown, .record-when-ready {
        position: absolute;
        left: 50%;
        top: ${isHandheld ? 50 : 80}%;
        transform: translate(-50%, -50%);
        color: white;
        display: flex;
        flex-direction: column;
        align-items: center;

        .starts-in, .when-ready {
          margin-bottom: ${standardSpacing}px;
          white-space: nowrap;
        }

        .recording-countdown-border {
          width: ${isHandheld ? 40 : 98}px;
          height: ${isHandheld ? 40 : 46}px;
          background-color: ${hexToRgbaString(danger, 0.5)};
          border-radius: 20px;
          display: flex;
          justify-content: center;
          align-items: center;

          .recording-countdown {
            width: ${isHandheld ? 30 : 88}px;
            height: ${isHandheld ? 30 : 36}px;
            background-color: ${danger};
            border-radius: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }

        .record-button-border {
          height: ${isHandheld ? 36 : 46}px;
          width: ${isHandheld ? 36 : 46}px;
          background-color: white;
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;

          .record-button {
            height: ${isHandheld ? 26 : 36}px;
            width: ${isHandheld ? 26 : 36}px;
            background-color: red;
            border-radius: 50%;
          }
        }
      }
    }

    .button-container {
      display: flex;
      margin-top: ${tripleSpacing}px;
      margin-bottom: ${doubleSpacing}px;

      button:first-of-type {
        margin-right: ${halfSpacing}px;
      }
    }

    ${handheld(css`
      .practice-activity-progress {
        margin-bottom: 0;
      }

      .details {
        width: 100%;
        padding-right: ${standardSpacing}px;
        padding-left: ${standardSpacing}px;

        .video-practice-title {
          margin-right: ${standardSpacing}px;
        }
      }

      .record-activity {
        padding-left: 20px;
        padding-right: 20px;
      }

      .button-container {
        width: 100%;
        flex-direction: column-reverse;
        padding-right: ${standardSpacing}px;
        padding-left: ${standardSpacing}px;

        button:first-of-type {
          margin-top: ${halfSpacing}px;
        }
      }
    `)};
  `;

  const dispatch = useAppDispatch();

  const { timeToRecord } = getTimeLimits(scenario);
  const timeToRecordRounded = roundToMinutes(timeToRecord);

  const isMounted = useRef(false);

  const [mediaStream, setMediaStream] = useState<MediaStream>();
  const recorder = useMediaRecorder(mediaStream, MediaSourceType.CAMERA_MIC, timeToRecord);

  const [preRecordCountdown, startCountdown, pauseCountdown, resumeCountdown] = useTimer();
  const [recordCountdown, startTimer, pauseTimer, resumeTimer] = useTimer();
  const [playCountdownBeep] = useCountdownBeep();

  const pause = () => {
    if (recordState === RecordState.RUNNING || recordState === RecordState.COUNTING_DOWN) {
      setRecordState(RecordState.PAUSED);
      if (preRecordCountdown) {
        pauseCountdown();
      } else if (recordCountdown) {
        recorder.pauseRecording();
        pauseTimer();
      }
    }
  };

  const resume = () => {
    if (recordState === RecordState.PAUSED) {
      recorder.resumeRecording();
      if (preRecordCountdown) {
        setRecordState(RecordState.COUNTING_DOWN);
        resumeCountdown();
      } else if (recordCountdown) {
        setRecordState(RecordState.RUNNING);
        resumeTimer();
      }
    }
  };

  const stopRecordingCallback = () => {
    stopMediaStreams(mediaStream);
    setMediaStream(null);
    recorder.destroyCurrentRecorder();
    setRecordState(RecordState.DONE);
  };

  const stopRecording = (proceedToSubmit = false) => {
    recorder.stopRecording()
      .then((blob: Blob) => {
        stopRecordingCallback();
        if (proceedToSubmit) {
          onStopRecording({ blob, timeElapsed: timeToRecord - recordCountdown });
        }
      });
  };

  const preRecordCountdownCallback = () => {
    startTimer(timeToRecord, () => stopRecording(true));
    recorder.startRecording();
    setRecordState(RecordState.RUNNING);
  };

  const startPreRecordCountdown = () => {
    if (mode === RecordMode.SCREEN) {
      playCountdownBeep();
    }
    startCountdown(3, preRecordCountdownCallback);
    setRecordState(RecordState.COUNTING_DOWN);
  };

  const onStopScreenRecording = useCallback(() => {
    if (isMounted.current) {
      setMediaStream(null);
    }

    if (recordState === RecordState.NOT_STARTED
      || recordState === RecordState.COUNTING_DOWN) {
      selectRecordingMode();
    } else {
      stopRecording(true);
    }
  }, [recordState]);

  useEffect(() => {
    window.stopScreenStreamCallback = () => {
      window.stopScreenStreamCallback = null;

      onStopScreenRecording();
    };
  }, [onStopScreenRecording]);

  useMountEffect(() => {
    const getMediaStream = () => {
      setMediaStream(null);
      requestMediaStream(
        mode === RecordMode.SCREEN
          ? MediaSourceType.SCREEN_MIC
          : MediaSourceType.CAMERA_MIC,
      ).then((stream: MediaStream) => {
        setMediaStream(stream);
        recorder.initializeRecorder(stream);

        if (practiceType === VideoPracticeOption.ON_SPOT) {
          startPreRecordCountdown();
        }

        if (mode === RecordMode.SCREEN) {
          window.stopScreenStreamCallback = () => {
            window.stopScreenStreamCallback = null;

            onStopScreenRecording();
          };
        }
      }).catch(error => {
        if (mode === RecordMode.SCREEN) {
          if (error.type === 'audio' && error.name === 'NotFoundError') {
            dispatch(openConfirmationDialog({
              confirmButtonVariant: 'primary',
              title: t.FILE_UPLOAD.CANNOT_RECORD.TITLE(),
              bodyText: t.FILE_UPLOAD.CANNOT_RECORD.AUDIO_NOT_FOUND(),
              confirmText: t.FORM.CONTINUE(),
              onConfirm: () => getMediaStream(),
              onCancel: () => selectRecordingMode(),
            }));
          } else if (error.type === 'audio' && error.name === 'NotAllowedError') {
            dispatch(openConfirmationDialog({
              confirmButtonVariant: 'primary',
              title: t.FILE_UPLOAD.CANNOT_RECORD.TITLE(),
              bodyText: t.FILE_UPLOAD.CANNOT_RECORD.AUDIO_NOT_ALLOWED(),
              confirmText: t.FORM.CONTINUE(),
              onConfirm: () => getMediaStream(),
              onCancel: () => selectRecordingMode(),
            }));
          } else if (error.name === 'NotAllowedError') {
            dispatch(openConfirmationDialog({
              title: t.FILE_UPLOAD.CANNOT_RECORD.TITLE(),
              bodyText: t.FILE_UPLOAD.CANNOT_RECORD.SCREEN_NOT_ALLOWED(),
              confirmText: t.FORM.CONTINUE(),
              onConfirm: () => getMediaStream(),
              onCancel: () => selectRecordingMode(),
            }));
          }
        } else if (error.name === 'NotFoundError') {
          dispatch(openConfirmationDialog({
            title: t.FILE_UPLOAD.CANNOT_RECORD.TITLE(),
            bodyText: t.FILE_UPLOAD.CANNOT_RECORD.VIDEO_NOT_FOUND(),
            confirmText: t.FORM.CONTINUE(),
            onConfirm: () => getMediaStream(),
            onCancel: () => selectRecordingMode(),
          }));
        } else if (error.name === 'NotAllowedError') {
          dispatch(openConfirmationDialog({
            title: t.FILE_UPLOAD.CANNOT_RECORD.TITLE(),
            bodyText: t.FILE_UPLOAD.CANNOT_RECORD.VIDEO_NOT_ALLOWED(),
            confirmText: t.FORM.CONTINUE(),
            onConfirm: () => getMediaStream(),
            onCancel: () => selectRecordingMode(),
          }));
        }
      });
    };

    getMediaStream();
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => () => {
    stopMediaStreams(mediaStream);
  }, [mediaStream]);

  useImperativeHandle(ref, () => ({
    pause,
    resume,
    stopRecording,
  }));

  const isButtonDisabled = recordState !== 'running' || !recordCountdown;
  const isRetryDisabled = isButtonDisabled || (practiceType === VideoPracticeOption.ON_SPOT && take >= activity.maxTries);

  let countdown;
  switch (recordState) {
    case RecordState.NOT_STARTED:
    case RecordState.COUNTING_DOWN:
      countdown = timeToRecord;
      break;
    case RecordState.DONE:
      countdown = 0;
      break;
    default:
      countdown = recordCountdown;
      break;
  }

  useEffect(() => {
    if (countdown === 3 && mode === RecordMode.SCREEN) {
      playCountdownBeep();
    }
  }, [countdown]);

  const paused = recordState === RecordState.NOT_STARTED || recordState === RecordState.DONE || recordState === RecordState.PAUSED;

  return (
    <div css={styles}>
      <PracticeActivityProgress modalStepIndex={modalStepIndex} />
      <div className='course-title-small text-white'>{t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.TITLE(timeToRecordRounded)}</div>
      <div className='details'>
        <VideoPracticeTitle
          practiceType={practiceType}
          activityTitle={scenario.title}
          activity={activity}
          take={take}
        />
        <CountdownTimer countdownRemainingSeconds={countdown} paused={paused} />
      </div>
      <div className='record-activity d-flex justify-content-center'>
        {mediaStream && (
          <React.Fragment>
            {mode === RecordMode.SCREEN ? (
              <React.Fragment>
                {(recordState === RecordState.NOT_STARTED || preRecordCountdown) ? (
                  <div className='video-stream-mask' />
                ) : ''}
                <div className='d-flex align-items-center text-white page-title'>
                  {preRecordCountdown ? (
                    <React.Fragment>
                      <span className='screen-recording-desc text-center'>
                        {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.STARTS_IN.SCREEN()}
                      </span>
                      <div className='record-countdown'>
                        <div className='recording-countdown-border'>
                          <div className={`recording-countdown ${isHandheld ? 'text-regular' : 'course-title-regular'}`}>
                            {preRecordCountdown}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  ) : ''}
                  {recordState === RecordState.RUNNING ? (
                    <span className='screen-recording-desc text-center'>
                      {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.RUNNING()}
                    </span>
                  ) : ''}
                </div>
                { (practiceType === VideoPracticeOption.WHEN_READY || practiceType === PracticeAgain.PRACTICE_AGAIN)
                && !preRecordCountdown && !recordCountdown ? (
                  <div className='when-ready-desc d-flex align-items-center text-white page-title'>
                    <div className='text-center'>
                      {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.WHEN_READY()}
                    </div>
                    <div className='record-when-ready'>
                      <ClickableContainer onClick={startPreRecordCountdown} className='record-when-ready record-button-border'>
                        <div className='record-button' />
                      </ClickableContainer>
                    </div>
                  </div>
                  ) : ''}
                {!!recordCountdown && (
                  <RecordingIndicator />
                )}
                {recordCountdown && timeToRecord > 0 && (
                  <PercentageIndicator
                    timeLimit={recordCountdown && timeToRecord}
                    recordState={recordState}
                  />
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <VideoStreamPreview
                  recordState={recordState}
                  timeLimit={recordCountdown && timeToRecord}
                  mediaStream={mediaStream}
                  showRecordIcon={!!recordCountdown}
                  showAudioVisualizer
                  width={720}
                  previewPrimaryOnly
                />
                {preRecordCountdown && (
                <div className='video-stream-mask d-flex justify-content-center'>
                  <div className='when-ready-desc d-flex align-items-center text-white page-title'>
                    <div className={`starts-in ${isHandheld ? 'text-regular' : 'page-title'} text-center`}>
                      {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.STARTS_IN.CAMERA()}
                    </div>
                    <div className='record-countdown'>
                      <div className='recording-countdown-border'>
                        <div className={`recording-countdown ${isHandheld ? 'text-regular' : 'course-title-regular'}`}>
                          {preRecordCountdown}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
                {(practiceType === VideoPracticeOption.WHEN_READY || practiceType === PracticeAgain.PRACTICE_AGAIN)
              && !preRecordCountdown && !recordCountdown && (
              <div className='video-stream-mask d-flex justify-content-center'>
                <div className='when-ready-desc d-flex align-items-center text-white page-title'>
                  <div className={`when-ready ${isHandheld ? 'text-regular' : 'page-title'}`}>
                    {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.WHEN_READY()}
                  </div>
                  <div className='record-when-ready'>
                    <ClickableContainer onClick={startPreRecordCountdown} className='record-button-border'>
                      <div className='record-button' />
                    </ClickableContainer>
                  </div>
                </div>
              </div>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
      <div className='button-container'>
        <NvPopover
          showOnHover
          enabled={!isRetryDisabled}
          placement='top'
          content={(
            <div>{t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RETRY.DETAILS()}</div>
          )}
        >
          <Button
            block={isHandheld}
            variant='secondary'
            disabled={isRetryDisabled}
            onClick={onRetry}
            pendo-tag-name={config.pendo.practice.retry}
          >
            {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RETRY.TITLE()}
          </Button>
        </NvPopover>
        <Button
          block={isHandheld}
          onClick={() => stopRecording(true)}
          disabled={isButtonDisabled}
          pendo-tag-name={config.pendo.practice.endRecording}
        >
          {t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.RECORD.END_RECORDING()}
        </Button>
      </div>
    </div>
  );
});

export default RecordPracticeActivity;
